import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { getTodayJobs, clockIn, clockOut } from '../../../services/contractorService';
import Header from '../../../components/ui/Header';
import Sidebar from '../../../components/ui/Sidebar';
import { ContractorJobHierarchy } from '../../../components/contractor/jobs';

/**
 * TodayJobs
 *
 * Displays all jobs assigned to the contractor for the current day
 * using the hierarchical Job Site → Job → Instance layout.
 * Shows contact person information for each job.
 */
const TodayJobs = () => {
  const { profile } = useAuth();
  const { currentCompany } = useCompany();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();

  const highlightId = searchParams.get('highlight');

  // Fetch today's jobs
  const fetchJobs = useCallback(async () => {
    if (!profile?.id || !currentCompany?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const timezone = currentCompany?.timezone || 'Australia/Sydney';
      const data = await getTodayJobs(profile.id, currentCompany.id, timezone);
      setJobs(data);
    } catch (err) {
      console.error('Error loading today jobs:', err);
      setError('Failed to load today\'s jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [profile?.id, currentCompany?.id, currentCompany?.timezone]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Clock In handler with geolocation
  const handleClockIn = useCallback(async (jobId) => {
    const executeClockIn = async (location) => {
      try {
        await clockIn(jobId, location);
        await fetchJobs();
      } catch (err) {
        console.error('Error clocking in:', err);
        setError('Failed to clock in. Please try again.');
      }
    };

    try {
      if (navigator?.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            await executeClockIn({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
          },
          async () => {
            // Clock in without location if permission denied
            await executeClockIn(null);
          },
          { timeout: 10000, enableHighAccuracy: false }
        );
      } else {
        await executeClockIn(null);
      }
    } catch (err) {
      console.error('Error clocking in:', err);
      setError('Failed to clock in. Please try again.');
    }
  }, [fetchJobs]);

  // Clock Out handler with geolocation
  const handleClockOut = useCallback(async (jobId) => {
    try {
      const executeClockOut = async (location) => {
        await clockOut(jobId, location);
        await fetchJobs();
      };

      if (navigator?.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            await executeClockOut({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
          },
          async () => {
            await executeClockOut(null);
          }
        );
      } else {
        await executeClockOut(null);
      }
    } catch (err) {
      console.error('Error clocking out:', err);
      setError('Failed to clock out. Please try again.');
    }
  }, [fetchJobs]);

  // Calculate completion stats
  const completedJobs = jobs?.filter(j =>
    j?.status === 'submitted' || j?.status === 'approved'
  )?.length || 0;
  const totalJobs = jobs?.length || 0;

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <Header />

        <main className="flex-1 overflow-y-auto overflow-x-hidden pt-16 pl-0 lg:pl-60">
          <div className="max-w-full px-4 py-6 sm:px-6 sm:py-8 sm:container sm:mx-auto">
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Today's Jobs</h1>
              <p className="text-text-secondary mt-2">Manage and execute your jobs for today</p>
              <div className="mt-4 flex items-center space-x-2 text-lg">
                <span className="text-text-primary font-semibold">{completedJobs}/{totalJobs}</span>
                <span className="text-text-secondary">Jobs Completed Today</span>
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 mb-6">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {/* Job Hierarchy */}
            {!loading && !error && (
              <ContractorJobHierarchy
                instances={jobs}
                onClockIn={handleClockIn}
                onClockOut={handleClockOut}
                mode="today"
                highlightInstanceId={highlightId}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default TodayJobs;
